import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  KeyRound,
  LogOut,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  Search,
  Share2,
  Copy,
  X as XIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  fetchCustomers,
  fetchOverview,
  fetchProducts,
  getDashboardToken,
  setDashboardToken,
  clearDashboardToken,
  readDemoParams,
  createDemoLink,
} from "@/lib/dashboard";
import {
  Card,
  CardHeader,
  KpiCard,
  Pill,
  SectionTitle,
} from "@/components/dashboard/Primitives";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "products", label: "Product fit" },
  { id: "customers", label: "Customers" },
];

// ---------- Gate ----------
function PasswordGate({ onUnlock, demoLinkInvalid = false }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      setDashboardToken(password);
      // Probe overview to verify the token before keeping it
      await fetchOverview(true);
      onUnlock();
    } catch (err) {
      const status = err?.response?.status;
      clearDashboardToken();
      if (status === 401) setError("Incorrect password.");
      else if (status === 503) setError("Dashboard is not yet enabled. Contact ops.");
      else setError("Could not verify password. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="dashboard-gate"
      className="min-h-screen bg-[#FAFAFA] grid place-items-center px-6"
    >
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-2xl border border-black/10 bg-white p-8"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-neutral-500">
          <KeyRound className="h-3 w-3" />
          Fittin · Pilot dashboard
        </div>
        <h1 className="mt-3 text-2xl tracking-[-0.02em] font-medium">
          {demoLinkInvalid ? "Demo link expired" : "Restricted access"}
        </h1>
        <p className="mt-2 text-[13px] text-neutral-600">
          {demoLinkInvalid
            ? "This demo link is invalid or has expired. Ask the Fittin team for a fresh link, or sign in with the operator password below."
            : "Enter the pilot password to view fit intelligence for your catalog."}
        </p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="dashboard-password-input"
          placeholder="Password"
          className="mt-6 w-full bg-transparent text-[16px] placeholder:text-neutral-300 text-[#0A0A0A] border-b border-black/15 focus:border-black/70 transition-colors py-2"
        />
        {error && (
          <div data-testid="dashboard-password-error" className="mt-2 text-[12px] text-red-600">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="dashboard-password-submit"
          className="mt-6 w-full rounded-full bg-[#0A0A0A] text-white px-4 py-2.5 text-[13px] hover:bg-neutral-800 transition-all active:scale-[0.99] disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Open dashboard"}
        </button>
        <Link to="/" className="mt-4 block text-center text-[12px] text-neutral-500 hover:text-[#0A0A0A]">
          Back to site
        </Link>
      </motion.form>
    </main>
  );
}

// ---------- Section: Overview ----------
function Overview({ demo, demoParams }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchOverview(demo, demoParams)
      .then((d) => mounted && setData(d))
      .catch((e) => mounted && setErr(e));
    return () => {
      mounted = false;
    };
  }, [demo, demoParams]);

  if (err) return <ErrorState msg="Could not load overview." />;
  if (!data) return <Loader />;

  const k = data.kpis || {};
  return (
    <div data-testid="dashboard-overview">
      <SectionTitle hint="Top-line operational signal across your fit intelligence layer.">
        Overview
      </SectionTitle>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Fit confidence"
          testid="kpi-confidence"
          value={`${Math.round((k.fit_confidence_score || 0) * 100)}%`}
          hint="Share of profiles with high-confidence size signal"
          trend={demo ? "+4.2pp" : undefined}
        />
        <KpiCard
          label="Returns prevented"
          testid="kpi-returns"
          value={(k.returns_prevented || 0).toLocaleString()}
          hint="Versus pre-Fittin baseline"
          trend={demo ? "+18%" : undefined}
        />
        <KpiCard
          label="Conversion lift"
          testid="kpi-lift"
          value={`+${Math.round((k.conversion_lift || 0) * 100)}%`}
          hint="Scanners vs non-scanners"
          trend={demo ? "+2.1pp" : undefined}
        />
        <KpiCard
          label="Profiles created"
          testid="kpi-profiles"
          value={(k.profiles_created || 0).toLocaleString()}
          hint="FitIDs across your shoppers"
          trend={demo ? "+612 / wk" : undefined}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="chart-return-trend" className="lg:col-span-2 p-0">
          <CardHeader
            eyebrow="Return rate"
            title="Before vs after Fittin"
            action={<Pill tone="dark">{demo ? "Demo data" : "Live"}</Pill>}
          />
          <div className="px-3 pb-5 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.return_trend || []} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#999" fontSize={11} tickLine={false} axisLine={false} unit="%" />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "rgba(0,0,0,0.15)" }} />
                <Line type="monotone" dataKey="before" stroke="#A3A3A3" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="after" stroke="#0A0A0A" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card testid="chart-scan-completion" className="p-0">
          <CardHeader eyebrow="Scan completion" title="Last 7 days" />
          <div className="px-3 pb-5 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.scan_completion || []} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#999" fontSize={11} tickLine={false} axisLine={false} domain={[0, 1]}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${Math.round(v * 100)}%`} />
                <Bar dataKey="value" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="chart-recommendation-funnel" className="lg:col-span-2 p-0">
          <CardHeader eyebrow="Fit recommendation usage" title="Funnel" />
          <div className="px-3 pb-5 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.recommendation_usage || []} layout="vertical" margin={{ top: 10, right: 16, left: 24, bottom: 0 }}>
                <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="label" stroke="#444" fontSize={11} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => v.toLocaleString()} />
                <Bar dataKey="value" fill="#0A0A0A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div className="space-y-4">
          {(data.ai_insights || []).slice(0, 3).map((ins, i) => (
            <Card key={i} testid={`ai-insight-${i}`} className="p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                <Sparkles className="h-3 w-3" />
                {ins.tag || "AI insight"}
              </div>
              <div className="mt-2 text-[13.5px] font-medium tracking-tight">{ins.title}</div>
              <p className="mt-1 text-[13px] text-neutral-600 leading-relaxed">{ins.body}</p>
            </Card>
          ))}
          {(!data.ai_insights || data.ai_insights.length === 0) && (
            <Card className="p-5">
              <div className="text-[12px] text-neutral-500">
                Insights appear as Fittin sees more shoppers and connected catalogs.
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Section: Products ----------
function Products({ demo, demoParams }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({ brand: "", category: "", risk: "", foot_type: "" });
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    setRows(null);
    fetchProducts(demo, Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), demoParams)
      .then((d) => mounted && setRows(d.rows || []))
      .catch((e) => mounted && setErr(e));
    return () => {
      mounted = false;
    };
  }, [demo, filters, demoParams]);

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        r.brand.toLowerCase().includes(needle) ||
        r.id.toLowerCase().includes(needle)
    );
  }, [rows, q]);

  if (err) return <ErrorState msg="Could not load products." />;

  return (
    <div data-testid="dashboard-products">
      <SectionTitle hint="Where the friction is. Per-product fit accuracy and return-risk signals.">
        Product fit intelligence
      </SectionTitle>

      <Card className="p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <SearchInput value={q} onChange={setQ} />
          <FilterSelect
            label="Brand"
            value={filters.brand}
            onChange={(v) => setFilters({ ...filters, brand: v })}
            options={["", "Adidas", "Nike", "Berwick", "Crockett & Jones", "Allen Edmonds", "Common Projects"]}
            testid="filter-brand"
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            onChange={(v) => setFilters({ ...filters, category: v })}
            options={["", "Sneakers", "Loafers", "Boots", "Dress"]}
            testid="filter-category"
          />
          <FilterSelect
            label="Return risk"
            value={filters.risk}
            onChange={(v) => setFilters({ ...filters, risk: v })}
            options={["", "low", "medium", "high"]}
            testid="filter-risk"
          />
          <FilterSelect
            label="Foot type"
            value={filters.foot_type}
            onChange={(v) => setFilters({ ...filters, foot_type: v })}
            options={["", "low", "medium", "high"]}
            optionsLabels={["Any", "Narrow only", "Standard", "Wide-friendly"]}
            testid="filter-foot-type"
          />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {!filteredRows ? (
          <div className="p-12 grid place-items-center"><Loader /></div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center text-[13px] text-neutral-500">
            {demo ? "No products match the filters." : "No catalog connected yet. Connect your retailer catalog to see product-level intelligence."}
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="products-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-neutral-500 border-b border-black/10">
                <th className="px-5 py-3 font-medium">Product</th>
                <th className="px-5 py-3 font-medium">Fit accuracy</th>
                <th className="px-5 py-3 font-medium">Return risk</th>
                <th className="px-5 py-3 font-medium">Skew</th>
                <th className="px-5 py-3 font-medium">Wide-foot</th>
                <th className="px-5 py-3 font-medium">Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} data-testid={`product-row-${r.id}`} className="border-b border-black/5 hover:bg-black/[0.015]">
                  <td className="px-5 py-4">
                    <div className="font-medium text-[#0A0A0A]">{r.name}</div>
                    <div className="text-[11.5px] text-neutral-500">{r.brand} · {r.category} · {r.samples?.toLocaleString?.() || 0} samples</div>
                  </td>
                  <td className="px-5 py-4">
                    <FitBar value={r.fit_accuracy} />
                  </td>
                  <td className="px-5 py-4">
                    <Pill tone={r.return_risk}>{r.return_risk}</Pill>
                  </td>
                  <td className="px-5 py-4 text-neutral-700">
                    {r.fit_skew === "small" && "Runs small"}
                    {r.fit_skew === "true" && "True to size"}
                    {r.fit_skew === "large" && "Runs large"}
                  </td>
                  <td className="px-5 py-4">
                    <Pill tone={r.wide_compat === "high" ? "low" : r.wide_compat === "medium" ? "medium" : "high"}>
                      {r.wide_compat}
                    </Pill>
                  </td>
                  <td className="px-5 py-4 font-mono text-[12.5px]">{r.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ---------- Section: Customers ----------
function Customers({ demo, demoParams }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchCustomers(demo, demoParams)
      .then((d) => mounted && setData(d))
      .catch((e) => mounted && setErr(e));
    return () => {
      mounted = false;
    };
  }, [demo, demoParams]);

  if (err) return <ErrorState msg="Could not load customer insights." />;
  if (!data) return <Loader />;

  const empty = !data.common_sizes?.length && !data.foot_widths?.length;

  return (
    <div data-testid="dashboard-customers">
      <SectionTitle hint="Who your shoppers are, by foot anatomy and fit preference.">
        Customer insights
      </SectionTitle>

      {empty && (
        <Card className="p-12 text-center">
          <div className="text-[13px] text-neutral-500">No customer data yet.</div>
        </Card>
      )}

      {!empty && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card testid="dist-foot-widths" className="p-0">
              <CardHeader eyebrow="Distribution" title="Foot widths" />
              <div className="px-3 pb-5 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.foot_widths || []}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#999" fontSize={11} tickLine={false} axisLine={false}
                      tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${Math.round(v * 100)}%`} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {(data.foot_widths || []).map((_, i) => (
                        <Cell key={i} fill={["#737373", "#0A0A0A", "#404040"][i] || "#0A0A0A"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card testid="dist-fit-pref" className="p-0">
              <CardHeader eyebrow="Preference" title="Fit preference" />
              <div className="px-3 pb-5 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.fit_preferences || []}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#999" fontSize={11} tickLine={false} axisLine={false}
                      tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${Math.round(v * 100)}%`} />
                    <Bar dataKey="value" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card testid="dist-geography" className="p-0">
              <CardHeader eyebrow="Geography" title="Top regions" />
              <div className="px-6 pb-6 pt-2">
                {(data.geography || []).map((g) => (
                  <div key={g.label} className="flex items-center justify-between py-2 border-b last:border-b-0 border-black/5">
                    <span className="text-[12.5px] text-neutral-700">{g.label}</span>
                    <span className="text-[12px] font-mono text-neutral-500">
                      {Math.round(g.value * 100)}%
                    </span>
                  </div>
                ))}
                {(!data.geography || data.geography.length === 0) && (
                  <div className="text-[12px] text-neutral-500 py-3">Pending more sample size.</div>
                )}
              </div>
            </Card>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card testid="dist-common-sizes" className="lg:col-span-2 p-0">
              <CardHeader eyebrow="Volume" title="Most common sizes" />
              <div className="px-3 pb-5 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.common_sizes || []}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#999" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => v.toLocaleString()} />
                    <Bar dataKey="value" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card testid="friction-segments" className="p-5">
              <CardHeader eyebrow="High-friction segments" title="Where returns concentrate" />
              <div className="px-1 pb-1">
                {(data.friction_segments || []).map((s, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 py-3 border-b last:border-b-0 border-black/5">
                    <div className="text-[13px] text-neutral-800 max-w-[260px] leading-snug">{s.label}</div>
                    <div className="text-right">
                      <div className="text-[12px] font-mono">{Math.round(s.return_rate * 100)}%</div>
                      <div className="text-[10.5px] text-neutral-500">{Math.round(s.share * 100)}% of base</div>
                    </div>
                  </div>
                ))}
                {(!data.friction_segments || data.friction_segments.length === 0) && (
                  <div className="text-[12px] text-neutral-500 py-3">No friction segments detected yet.</div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Helpers ----------
const tooltipStyle = {
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 12,
  padding: "8px 12px",
  fontSize: 12,
  fontFamily: "Satoshi, sans-serif",
  boxShadow: "0 8px 32px -16px rgba(10,10,10,0.15)",
};

function Loader() {
  return (
    <div className="flex items-center gap-2 text-neutral-500 text-[13px] py-12 justify-center">
      <RefreshCw className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  );
}

function ErrorState({ msg }) {
  return (
    <Card className="p-8">
      <div className="flex items-center gap-2 text-amber-700">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-[13px]">{msg}</span>
      </div>
    </Card>
  );
}

function FitBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2 max-w-[140px]">
      <div className="flex-1 h-[5px] rounded-full bg-black/5 overflow-hidden">
        <div className="h-full bg-[#0A0A0A]" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11.5px] font-mono text-neutral-700 w-9 text-right">{pct}%</span>
    </div>
  );
}

function SearchInput({ value, onChange }) {
  return (
    <div className="relative">
      <Search className="h-3.5 w-3.5 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="filter-search"
        placeholder="Search products"
        className="w-full pl-9 pr-3 py-2 text-[13px] bg-white border border-black/10 rounded-full focus:border-black/40 focus:outline-none transition-colors"
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, optionsLabels, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 text-[13px] bg-white border border-black/10 rounded-full focus:border-black/40 focus:outline-none transition-colors text-neutral-700 appearance-none pr-8"
      style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
      }}
    >
      {options.map((opt, i) => (
        <option key={opt || `any-${i}`} value={opt}>
          {optionsLabels?.[i] ?? (opt || `Any ${label.toLowerCase()}`)}
        </option>
      ))}
    </select>
  );
}

// ---------- Top-level Dashboard ----------
export default function Dashboard() {
  // Demo-link mode reads ?demo=<slug>&t=<token> from the URL and bypasses the password gate.
  const [demoParams] = useState(() => readDemoParams());
  const isDemoLink = !!demoParams;
  const [unlocked, setUnlocked] = useState(isDemoLink || !!getDashboardToken());
  const [section, setSection] = useState("overview");
  const [demo, setDemo] = useState(true); // default to demo for pitch quality
  const [shareOpen, setShareOpen] = useState(false);
  const [prospectName, setProspectName] = useState(null); // pulled from overview response

  // If a token exists (operator mode), verify it on mount; if it's stale, drop back to gate.
  useEffect(() => {
    if (!unlocked || isDemoLink) return;
    fetchOverview(true).catch((e) => {
      if (e?.response?.status === 401) {
        clearDashboardToken();
        setUnlocked(false);
      }
    });
  }, [unlocked, isDemoLink]);

  // For demo-link mode, fetch prospect display name once
  useEffect(() => {
    if (!isDemoLink) return;
    fetchOverview(true, demoParams)
      .then((d) => setProspectName(d?.prospect || null))
      .catch(() => {
        toast.error("This demo link is invalid or has expired.");
        setUnlocked(false);
      });
  }, [isDemoLink, demoParams]);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} demoLinkInvalid={isDemoLink} />;
  }

  const signOut = () => {
    clearDashboardToken();
    setUnlocked(false);
    toast.info("Signed out of dashboard.");
  };

  return (
    <main
      data-testid="dashboard-page"
      className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]"
    >
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-md border-b border-black/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid="dashboard-brand" className="flex items-center gap-2">
            <span className="h-5 w-5 rounded-full bg-[#0A0A0A] grid place-items-center">
              <span className="h-1 w-1 rounded-full bg-[#FAFAFA]" />
            </span>
            <span className="text-[15px] font-medium tracking-tight">Fittin</span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 ml-2 hidden md:inline">
              {isDemoLink ? `${prospectName || demoParams.slug} preview` : "Pilot dashboard"}
            </span>
          </Link>

          <div className="flex items-center gap-2">
            {!isDemoLink && <DemoToggle demo={demo} setDemo={setDemo} />}
            {!isDemoLink && (
              <button
                onClick={() => setShareOpen(true)}
                data-testid="dashboard-share-open"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] hover:bg-neutral-50 transition-all"
              >
                <Share2 className="h-3 w-3" />
                <span className="hidden md:inline">Share demo</span>
              </button>
            )}
            {!isDemoLink && (
              <button
                onClick={signOut}
                data-testid="dashboard-signout"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] hover:bg-neutral-50 transition-all"
              >
                <LogOut className="h-3 w-3" />
                <span className="hidden md:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>

        <nav className="mx-auto max-w-7xl px-6 md:px-8 flex items-center gap-1 -mb-px overflow-x-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`dashboard-tab-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`relative px-3 py-2.5 text-[13px] transition-colors ${
                section === s.id ? "text-[#0A0A0A]" : "text-neutral-500 hover:text-[#0A0A0A]"
              }`}
            >
              {s.label}
              {section === s.id && (
                <motion.span
                  layoutId="dashboard-tab-underline"
                  className="absolute left-2 right-2 -bottom-px h-[2px] bg-[#0A0A0A] rounded-full"
                />
              )}
            </button>
          ))}
        </nav>
      </header>

      {isDemoLink && (
        <div
          data-testid="dashboard-prospect-banner"
          className="bg-[#0A0A0A] text-white"
        >
          <div className="mx-auto max-w-7xl px-6 md:px-8 py-2 text-[12px] flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            <span>
              Personalized preview for{" "}
              <strong className="font-medium">{prospectName || demoParams.slug}</strong>. Demo data — connect a catalog to see live numbers.
            </span>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6 md:px-8 pb-24">
        {section === "overview" && <Overview demo={isDemoLink ? true : demo} demoParams={demoParams} />}
        {section === "products" && <Products demo={isDemoLink ? true : demo} demoParams={demoParams} />}
        {section === "customers" && <Customers demo={isDemoLink ? true : demo} demoParams={demoParams} />}
      </div>

      {shareOpen && <ShareDemoModal onClose={() => setShareOpen(false)} />}
    </main>
  );
}

// ---------- Share-demo Modal ----------
function ShareDemoModal({ onClose }) {
  const [prospect, setProspect] = useState("");
  const [adminToken, setAdminToken] = useState(() => {
    try {
      return sessionStorage.getItem("fittin_admin_token") || "";
    } catch {
      return "";
    }
  });
  const [generated, setGenerated] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!prospect.trim() || !adminToken.trim()) {
      setError("Both prospect name and admin token are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const data = await createDemoLink(prospect.trim(), adminToken.trim());
      try {
        sessionStorage.setItem("fittin_admin_token", adminToken.trim());
      } catch {
        /* ignore */
      }
      setGenerated(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Invalid admin token.");
      else setError("Could not generate link. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (!generated?.url) return;
    try {
      await navigator.clipboard.writeText(generated.url);
      toast.success("Link copied.");
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const expiresLabel = useMemo(() => {
    if (!generated?.expires_at) return null;
    const d = new Date(generated.expires_at * 1000);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }, [generated]);

  return (
    <div
      data-testid="dashboard-share-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-black/30 backdrop-blur-sm px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md rounded-2xl border border-black/10 bg-white p-7"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
              Share dashboard
            </div>
            <h2 className="mt-1 text-xl tracking-[-0.02em] font-medium">Create a prospect link</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="dashboard-share-close"
            className="text-neutral-400 hover:text-[#0A0A0A] transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {!generated && (
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                Prospect name
              </label>
              <input
                type="text"
                value={prospect}
                onChange={(e) => setProspect(e.target.value)}
                data-testid="dashboard-share-prospect"
                placeholder="ACME Co"
                className="mt-1 w-full bg-transparent text-[16px] placeholder:text-neutral-300 text-[#0A0A0A] border-b border-black/15 focus:border-black/70 transition-colors py-2"
              />
              <div className="mt-1 text-[11px] text-neutral-500">
                Used as the prospect's name in the dashboard's AI insight copy.
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
                Admin token
              </label>
              <input
                type="password"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                data-testid="dashboard-share-admin-token"
                placeholder="From Render env"
                className="mt-1 w-full bg-transparent text-[16px] placeholder:text-neutral-300 text-[#0A0A0A] border-b border-black/15 focus:border-black/70 transition-colors py-2"
              />
              <div className="mt-1 text-[11px] text-neutral-500">
                Cached in this browser session only.
              </div>
            </div>
            {error && (
              <div data-testid="dashboard-share-error" className="text-[12px] text-red-600">
                {error}
              </div>
            )}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={submitting}
                data-testid="dashboard-share-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-4 py-2 text-[13px] hover:bg-neutral-800 transition-all disabled:opacity-60"
              >
                {submitting ? "Generating…" : "Generate link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-[12.5px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {generated && (
          <div className="mt-6" data-testid="dashboard-share-result">
            <div className="text-[12.5px] text-neutral-600">
              Personalized link for <strong>{generated.prospect}</strong>. Expires{" "}
              {expiresLabel}.
            </div>
            <div className="mt-3 rounded-xl border border-black/10 bg-[#FAFAFA] p-3 flex items-center gap-2">
              <code
                data-testid="dashboard-share-url"
                className="flex-1 text-[11.5px] font-mono text-[#0A0A0A] break-all"
              >
                {generated.url}
              </code>
              <button
                onClick={copy}
                data-testid="dashboard-share-copy"
                className="inline-flex items-center gap-1 rounded-full bg-[#0A0A0A] text-white px-3 py-1.5 text-[11.5px] hover:bg-neutral-800 transition-all"
              >
                <Copy className="h-3 w-3" />
                Copy
              </button>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={() => setGenerated(null)}
                className="text-[12.5px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
              >
                Generate another
              </button>
              <button
                onClick={onClose}
                className="ml-auto inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-[13px] hover:bg-neutral-50 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function DemoToggle({ demo, setDemo }) {
  return (
    <div
      data-testid="dashboard-demo-toggle"
      className="inline-flex items-center rounded-full bg-white border border-black/10 p-0.5 text-[11px]"
    >
      <button
        onClick={() => setDemo(true)}
        data-testid="dashboard-demo-on"
        className={`px-3 py-1 rounded-full transition-colors ${
          demo ? "bg-[#0A0A0A] text-white" : "text-neutral-500 hover:text-[#0A0A0A]"
        }`}
      >
        Demo
      </button>
      <button
        onClick={() => setDemo(false)}
        data-testid="dashboard-demo-off"
        className={`px-3 py-1 rounded-full transition-colors ${
          !demo ? "bg-[#0A0A0A] text-white" : "text-neutral-500 hover:text-[#0A0A0A]"
        }`}
      >
        Live
      </button>
    </div>
  );
}
